import { useListView } from './core/ListViewProvider'
import {
  useQueryResponseData,
  useQueryResponseLoading,
  useQueryResponse,
} from './core/QueryResponseProvider'
import { fields, Application } from './core/_models'
import { CView } from '../../../../../_component/view/View'
import { updateApplication } from './core/_requests'
import { Mail, Phone, FileText, CheckCircle, XCircle } from 'lucide-react'
import { ApplicationDetailSkeleton } from '../../../../../_component/Skeleton'

export const ApplicationDetails = () => {
  const { itemIdToView } = useListView()
  const { refetch } = useQueryResponse()
  const applications = useQueryResponseData() as Application[]
  const isLoading = useQueryResponseLoading()

  const application = applications.find((app) => app.id === itemIdToView)

  if (isLoading) {
    return <ApplicationDetailSkeleton />
  }

  if (!application) {
    return (
      <div className='flex flex-col items-center justify-center h-full text-gray-500 opacity-60'>
        <FileText size={64} strokeWidth={1} className='mb-4' />
        <p className='text-lg font-medium'>Select an application to view details</p>
        <p className='text-sm'>All applicant information will be displayed here</p>
      </div>
    )
  }

  const updateStatus = async (status: string) => {
    if (!application.id) return
    try {
      await updateApplication({ ...application, status } as any)
      refetch()
    } catch (error) {
      console.error('Failed to update status:', error)
    }
  }

  return (
    <div className='flex flex-col h-full bg-white'>
      {/* Header with Quick Info and Actions */}
      <div className='p-6 border-b border-gray-200'>
        <div className='flex justify-between items-start mb-6'>
          <div>
            <h2 className='text-2xl font-bold text-gray-900 mb-1'>{application.full_name}</h2>
            <p className='text-[#008080] font-medium text-lg'>
              Applied for: {application.job?.title || 'Unknown Position'}
            </p>
          </div>
          <div className='flex gap-2'>
            <button
              onClick={() => updateStatus('accepted')}
              className='inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white font-bold rounded-md hover:bg-green-700 transition'
            >
              <CheckCircle size={18} />
              Accept
            </button>
            <button
              onClick={() => updateStatus('declined')}
              className='inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white font-bold rounded-md hover:bg-red-700 transition'
            >
              <XCircle size={18} />
              Decline
            </button>
          </div>
        </div>

        <div className='flex flex-wrap gap-6'>
          <div className='flex items-center gap-2 text-gray-600'>
            <Mail size={18} className='text-gray-400' />
            <span className='font-medium'>{application.email}</span>
          </div>
          <div className='flex items-center gap-2 text-gray-600'>
            <Phone size={18} className='text-gray-400' />
            <span className='font-medium'>{application.phone}</span>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className='flex-1 overflow-y-auto p-6'>
        <div className='max-w-4xl'>
          <div className=''>
            <h3 className='text-lg font-bold text-gray-800 mb-4 pb-2 border-b border-gray-100'>
              Application Details
            </h3>
            <CView isLoading={false} data={application} fields={fields} />
          </div>

          <div className='mt-12 pt-8 border-t border-gray-100 uppercase tracking-widest'>
            <h4 className='text-xs font-bold text-gray-400 mb-4'>Update Application Status</h4>
            <div className='flex flex-wrap gap-2'>
              {['applied', 'read', 'accepted', 'declined'].map((status) => (
                <button
                  key={status}
                  onClick={() => updateStatus(status)}
                  className={`
                                        px-4 py-1.5 rounded-full text-xs font-bold transition-all
                                        ${application.status === status
                      ? 'bg-[#008080] text-white shadow-md'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }
                                    `}
                >
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
